import * as React from 'react'
import {
  Block,
  CommonBlocksCompoundIndexes,
  ComponentBlock,
  DateBlock,
  GalleryView,
  InstanceBlock,
  ListBlock,
  isComponentBlock,
  normalizeStringPath,
  prettifyBlockType,
} from '@basehub/data/replicache/index'
import { BaseHubCollectionColumn } from '../table/types'
import { isoStringToDisplayDate } from '~/lib/date-utils'
import { Button, ButtonLink } from '~/sotano-kit/primitives/button'
import { useDashboardSlugs } from '~/hooks/use-dashboard-slugs'
import { useTabsStore } from '../../../../tabs/store'
import {
  buildYDocFromState,
  richTextExtensionsForTransformations,
  stateToBase64,
  yDocToPlainText,
} from '@basehub/tiptap-api-utils'
import { BlockIcon } from '~/components/block-icon'
import { useThumbnail } from '../use-thumbnail'
import { clsx } from 'clsx'
import { getBlockFromColumnId } from '@basehub/data/replicache/schema/block/list/utils'
import { TreeManager } from '@basehub/utils/tree/index'
import { useWorkingTreeStore } from '~/hooks-data/blocks-tree/provider'
import { idToSourceBlockId } from '@basehub/data/replicache/mutators/repo-space/block/mutators/copy-on-write'
import { Checkbox } from '~/sotano-kit/primitives/checkbox'
import { SimpleTooltip } from '~/sotano-kit/primitives/tooltip'
import { useRepoStore } from '../../../../repo-store'
import { BaseHubImage } from 'basehub/next-image'

import { Input } from '~/sotano-kit/primitives/input'
import { useSyncedValue } from '~/hooks/use-synced-value'
import { useRepoUndoManager } from '~/replicache/frontend/undo-redo/manager'
import { UpdateBlockParams } from '@basehub/data/replicache/mutators/repo-space/block/mutators/crud'
import { useRepoReplicache } from '~/hooks-data/use-repo-replicache'
import { useDndContext, useDraggable } from '@dnd-kit/core'
import { listBlockDndControllers } from '../dnd'
import { mergeRefs } from '~/lib/merge-refs'
import { BlockWrapperContextMenu } from '../../../block-wrapper-actions/context-menu'
import { EditorModeProvider } from '../../../../editor-mode-provider'
import { FileTextIcon } from '@radix-ui/react-icons'
import { formatBytes } from '~/lib/file-utils'

const CARD_ASPECT_RATIO = '222/110'

export const Card = ({
  parent,
  parentPath,
  block,
  columns,
  selectedView,
  isRenaming = false,
  onFinishedRenaming,
  className,
  treeManager,
  contextMenu = true,
  tabPrefix = '',
  index,
}: {
  parent: ListBlock
  parentPath: string
  block: InstanceBlock | ComponentBlock
  columns: BaseHubCollectionColumn[]
  selectedView: GalleryView
  isRenaming?: boolean
  onFinishedRenaming?: (value?: string) => void
  className?: string
  treeManager: TreeManager<Block, CommonBlocksCompoundIndexes> | null
  contextMenu?: boolean
  tabPrefix?: string
  index: number
}) => {
  const addTab = useTabsStore((s) => s.addTab)
  const thumbnailBlock = useThumbnail({
    templateId: parent.typeOptions.targetBlockId,
    row: block,
    selectedView,
    treeManager,
  })
  const { data } = useRepoReplicache()
  const replicache = data?.replicache
  const undoManager = useRepoUndoManager()
  const path = normalizeStringPath({
    path: parentPath + '/' + block.id,
    edgeId: block.id,
    includeEdge: true,
    format: 'string',
  })
  const rowSourceBlockId = idToSourceBlockId(block)
  const { active } = useDndContext()
  const draggable = useDraggable({
    id: `list-entry/${rowSourceBlockId}`,
    data: listBlockDndControllers.draggableDataSchema.parse({
      // for now, we'll only allow dragging one block at a time
      keysDragged: new Set([{ label: block.title, id: rowSourceBlockId }]),
      type: listBlockDndControllers.dndType,
      index,
      shouldDropTo: parent.id,
    }),
  })

  const {
    onChange,
    sync: _,
    valueRef: __,
    ...syncedInputProps
  } = useSyncedValue<HTMLInputElement>({
    customEventsKey: `synced-value:gallery-card:${idToSourceBlockId(block)}`,
    value: block.title,
    setValue: React.useCallback(
      async (v, { internalValueRef, customEventsKey }) => {
        if (!replicache?.mutate) return
        const getParams = (v: string): UpdateBlockParams => ({
          path,
          id: block.id,
          data: {
            type: block.type,
            title: v,
          },
          instanceChildTarget: block.instanceChildTarget,
          strategy: 'merge',
          isoNow: new Date().toISOString(),
        })
        const { undo } = await replicache.mutate.updateBlock(getParams(v))
        if (undo) {
          undoManager.current?.push({
            redo: [
              { op: 'updateBlock', args: getParams(internalValueRef.current) },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
            undo: [
              { op: 'blockBatchMutations', args: { mutations: undo } },
              {
                op: 'customWindowEvent',
                args: { name: customEventsKey, detail: { action: 'sync' } },
              },
            ],
          })
        }
      },
      [
        replicache?.mutate,
        path,
        block.id,
        block.type,
        block.instanceChildTarget,
        undoManager,
      ]
    ),
  })
  const isTemplate = parent.typeOptions.targetBlockId === block.id
  const cardRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    if (!isRenaming) return
    const input = cardRef.current?.querySelector<HTMLInputElement>(
      'input[role="card-title-input"]'
    )
    if (!input) return
    setTimeout(() => input.select())
  }, [isRenaming])

  const body = React.useMemo(() => {
    if (!treeManager) return null

    const template = treeManager.getNode(parent.typeOptions.targetBlockId, {
      required: true,
    }).value
    if (!template || !isComponentBlock(template)) return null

    let cols: BaseHubCollectionColumn[] = []
    if (!selectedView.thumbnailColumn) cols = columns
    else {
      columns.forEach((column) => {
        if (column.id === selectedView.thumbnailColumn) return

        cols.push(column)
      })
    }

    return cols
      .map((column, i) => {
        if (!treeManager) return

        if (column.id === '__title') {
          return (
            <p
              key={column.id}
              className={clsx(
                isRenaming && 'invisible',
                isTemplate ? 'text-dark-control-highlight' : 'text-dark-bright',
                'text-sm font-medium leading-normal line-clamp-2',
                i !== cols.length - 1 && 'not:only-child:!mb-3'
              )}
            >
              {block.title.trim() || 'Untitled'}
            </p>
          )
        }

        const { block: card, blockFromTemplate } = getBlockFromColumnId(
          column.id,
          { row: block, treeManager }
        )
        if (!card) return null

        switch (card.type) {
          case 'media':
            if (card.value.status === 'idle') return null
            if (card.value.status === 'uploading') {
              return (
                <BodyBlockWrapper parent={block} block={card} key={card.id}>
                  <span className="h-[30px] bg-dark-faint rounded-md text-xs text-dark-muted">
                    Uploading media
                  </span>
                </BodyBlockWrapper>
              )
            }

            if (card.value.type === 'video') {
              return (
                <BodyBlockWrapper
                  parent={block}
                  block={card}
                  key={card.id}
                  className="py-1"
                >
                  <video
                    src={card.value.url}
                    style={{
                      backgroundImage: `url(data:image/png;base64,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)`,
                      backgroundRepeat: 'repeat',
                      backgroundSize: '16px',
                    }}
                    controls={false}
                    autoPlay
                    playsInline
                    muted
                    className="pointer-events-none select-none h-[30px] w-[30px] border border-dark-border-solid group-hover:border-dark-elevated rounded-md"
                  />
                </BodyBlockWrapper>
              )
            }

            if (card.value.type === 'image') {
              return (
                <BodyBlockWrapper
                  parent={block}
                  block={card}
                  key={card.id}
                  className="text-xs text-dark-muted leading-normal line-clamp-3 py-1"
                >
                  <BaseHubImage
                    style={{
                      backgroundImage: `url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAMPmlDQ1BJQ0MgUHJvZmlsZQAASImVVwdYU8kWnluSkEBooUsJvQkiNYCUEFoA6V1UQhIglBgDQcVeFhVcu1jAhq6KKFhpFhRRLCyKvS8WVJR1sWBX3qSArvvK9+b75s5//znznzPnztx7BwC1ExyRKA9VByBfWCiODQmgJ6ek0klPAQ5QoAurHYdbIGJGR0cAWIbav5d31wEiba84SLX+2f9fiwaPX8AFAImGOINXwM2H+BAAeCVXJC4EgCjlzacUiqQYVqAlhgFCvEiKs+S4Uooz5HifzCY+lgVxGwBKKhyOOAsA1UuQpxdxs6CGaj/ETkKeQAiAGh1i3/z8STyI0yG2gTYiiKX6jIwfdLL+ppkxrMnhZA1j+VxkRSlQUCDK40z7P9Pxv0t+nmTIhxWsKtni0FjpnGHebuZOCpdiFYj7hBmRURBrQvxBwJPZQ4xSsiWhCXJ71JBbwII5AzoQO/E4geEQG0IcLMyLjFDwGZmCYDbEcIWgUwWF7HiI9SBexC8IilPYbBFPilX4QuszxSymgj/LEcv8Sn3dl+QmMBX6r7P5bIU+plqcHZ8EMQViiyJBYiTEqhA7FuTGhStsxhRnsyKHbMSSWGn8FhDH8oUhAXJ9rChTHByrsC/NLxiaL7YlW8COVOADhdnxofL8YG1cjix+OBfsEl/ITBjS4RckRwzNhccPDJLPHXvGFybEKXQ+iAoDYuVjcYooL1phj5vx80KkvBnErgVFcYqxeGIhXJByfTxTVBgdL48TL87hhEXL48GXgwjAAoGADiSwZoBJIAcIOvsa+uCdvCcYcIAYZAE+cFAwQyOSZD1CeI0DxeBPiPigYHhcgKyXD4og/3WYlV8dQKast0g2Ihc8gTgfhIM8eC+RjRIOe0sEjyEj+Id3DqxcGG8erNL+f88Psd8ZJmQiFIxkyCNdbciSGEQMJIYSg4m2uAHui3vjEfDqD6szzsA9h+bx3Z7whNBFeEi4Rugm3JoomCf+KcqxoBvqBytykfFjLnArqOmGB+A+UB0q4zq4AXDAXaEfJu4HPbtBlqWIW5oV+k/af5vBD09DYUd2IqNkXbI/2ebnkap2qm7DKtJc/5gfeawZw/lmDff87J/1Q/Z5sA3/2RJbhB3E2rGT2DnsKNYA6FgL1oh1YMekeHh1PZatriFvsbJ4cqGO4B/+hp6sNJMFTjVOvU5f5H2F/KnSdzRgTRJNEwuysgvpTPhF4NPZQq7jSLqzk7MLANLvi/z19SZG9t1AdDq+c/P/AMCnZXBw8Mh3LqwFgP0ecPs3fedsGPDToQzA2SauRFwk53DphQDfEmpwp+kDY2AObOB8nIE78Ab+IAiEgSgQD1LABBh9NlznYjAFzABzQQkoA8vBGrABbAbbwC6wFxwADeAoOAnOgAvgErgG7sDV0wNegH7wDnxGEISEUBEaoo+YIJaIPeKMMBBfJAiJQGKRFCQdyUKEiASZgcxHypCVyAZkK1KN7EeakJPIOaQLuYU8QHqR18gnFENVUC3UCLVCR6EMlImGo/HoeDQLnYwWowvQpeg6tArdg9ajJ9EL6DW0G32BDmAAU8Z0MFPMAWNgLCwKS8UyMTE2CyvFyrEqrBZrhs/5CtaN9WEfcSJOw+m4A1zBoXgCzsUn47PwJfgGfBdej7fhV/AHeD/+jUAlGBLsCV4ENiGZkEWYQighlBN2EA4TTsO91EN4RyQSdYjWRA+4F1OIOcTpxCXEjcQ64gliF/ERcYBEIumT7Ek+pCgSh1RIKiGtJ+0htZAuk3pIH5SUlUyUnJWClVKVhErzlMqVdisdV7qs9FTpM1mdbEn2IkeReeRp5GXk7eRm8kVyD/kzRYNiTfGhxFNyKHMp6yi1lNOUu5Q3ysrKZsqeyjHKAuU5yuuU9ymfVX6g/FFFU8VOhaWSpiJRWaqyU+WEyi2VN1Qq1YrqT02lFlKXUqupp6j3qR9UaaqOqmxVnups1QrVetXLqi/VyGqWaky1CWrFauVqB9UuqvWpk9Wt1FnqHPVZ6hXqTeo31Ac0aBqjNaI08jWWaOzWOKfxTJOkaaUZpMnTXKC5TfOU5iMaRjOnsWhc2nzadtppWo8WUctai62Vo1WmtVerU6tfW1PbVTtRe6p2hfYx7W4dTMdKh62Tp7NM54DOdZ1Puka6TF2+7mLdWt3Luu/1Ruj56/H1SvXq9K7pfdKn6wfp5+qv0G/Qv2eAG9gZxBhMMdhkcNqgb4TWCO8R3BGlIw6MuG2IGtoZxhpON9xm2GE4YGRsFGIkMlpvdMqoz1jH2N84x3i18XHjXhOaia+JwGS1SYvJc7o2nUnPo6+jt9H7TQ1NQ00lpltNO00/m1mbJZjNM6szu2dOMWeYZ5qvNm8177cwsRhrMcOixuK2JdmSYZltuday3fK9lbVVktVCqwarZ9Z61mzrYusa67s2VBs/m8k2VTZXbYm2DNtc2422l+xQOze7bLsKu4v2qL27vcB+o33XSMJIz5HCkVUjbzioODAdihxqHB446jhGOM5zbHB8OcpiVOqoFaPaR31zcnPKc9rudGe05uiw0fNGN49+7WznzHWucL7qQnUJdpnt0ujyytXele+6yfWmG81trNtCt1a3r+4e7mL3WvdeDwuPdI9KjxsMLUY0YwnjrCfBM8BztudRz49e7l6FXge8/vJ28M713u39bIz1GP6Y7WMe+Zj5cHy2+nT70n3Tfbf4dvuZ+nH8qvwe+pv78/x3+D9l2jJzmHuYLwOcAsQBhwPes7xYM1knArHAkMDSwM4gzaCEoA1B94PNgrOCa4L7Q9xCpoecCCWEhoeuCL3BNmJz2dXs/jCPsJlhbeEq4XHhG8IfRthFiCOax6Jjw8auGns30jJSGNkQBaLYUaui7kVbR0+OPhJDjImOqYh5Ejs6dkZsexwtbmLc7rh38QHxy+LvJNgkSBJaE9US0xKrE98nBSatTOpOHpU8M/lCikGKIKUxlZSamLojdWBc0Lg143rS3NJK0q6Ptx4/dfy5CQYT8iYcm6g2kTPxYDohPSl9d/oXThSnijOQwc6ozOjnsrhruS94/rzVvF6+D38l/2mmT+bKzGdZPlmrsnqz/bLLs/sELMEGwauc0JzNOe9zo3J35g7mJeXV5Svlp+c3CTWFucK2ScaTpk7qEtmLSkTdk70mr5ncLw4X7yhACsYXNBZqwR/5DomN5BfJgyLfooqiD1MSpxycqjFVOLVjmt20xdOeFgcX/zYdn86d3jrDdMbcGQ9mMmdunYXMypjVOtt89oLZPXNC5uyaS5mbO/f3eU7zVs57Oz9pfvMCowVzFjz6JeSXmhLVEnHJjYXeCzcvwhcJFnUudlm8fvG3Ul7p+TKnsvKyL0u4S87/OvrXdb8OLs1c2rnMfdmm5cTlwuXXV/it2LVSY2Xxykerxq6qX01fXbr67ZqJa86Vu5ZvXktZK1nbvS5iXeN6i/XL13/ZkL3hWkVARV2lYeXiyvcbeRsvb/LfVLvZaHPZ5k9bBFtubg3ZWl9lVVW+jbitaNuT7Ynb239j/Fa9w2BH2Y6vO4U7u3fF7mqr9qiu3m24e1kNWiOp6d2TtufS3sC9jbUOtVvrdOrK9oF9kn3P96fvv34g/EDrQcbB2kOWhyoP0w6X1iP10+r7G7IbuhtTGruawppam72bDx9xPLLzqOnRimPax5YdpxxfcHywpbhl4IToRN/JrJOPWie23jmVfOpqW0xb5+nw02fPBJ851c5sbznrc/boOa9zTecZ5xsuuF+o73DrOPy72++HO9076y96XGy85HmpuWtM1/HLfpdPXgm8cuYq++qFa5HXuq4nXL95I+1G903ezWe38m69ul10+/OdOXcJd0vvqd8rv294v+oP2z/qut27jz0IfNDxMO7hnUfcRy8eFzz+0rPgCfVJ+VOTp9XPnJ8d7Q3uvfR83POeF6IXn/tK/tT4s/KlzctDf/n/1dGf3N/zSvxq8PWSN/pvdr51fds6ED1w/13+u8/vSz/of9j1kfGx/VPSp6efp3whfVn31fZr87fwb3cH8wcHRRwxR/YrgMGKZmYC8HonANQUAGjwfEYZJz//yQoiP7PKEPhPWH5GlBV3AGrh/3tMH/y7uQHAvu3w+AX11dIAiKYCEO8JUBeX4Tp0VpOdK6WFCM8BW2K/ZuRngH9T5GfOH+L+uQVSVVfwc/sv6N18VQn/l7wAAAA4ZVhJZk1NACoAAAAIAAGHaQAEAAAAAQAAABoAAAAAAAKgAgAEAAAAAQAAACCgAwAEAAAAAQAAACAAAAAAj05AyQAAAFhJREFUWAntlkEKACAIBDV8Rf//p9UD1EOUlwk6LQvL5EoqInPf8Lh7qB1BVVO98o/U/UEkAAQgYFVPb3te+XkCCECgnYBVPX29J9oJEAACEOA/wAxAAAIL9Q4YXZojZ+kAAAAASUVORK5CYII=)`,
                      backgroundRepeat: 'repeat',
                      backgroundSize: '20px',
                    }}
                    src={card.value.url}
                    alt={card.value.altText ?? ''}
                    className="object-contain aspect-square border rounded-md border-dark-border-solid group-hover:border-dark-elevated"
                    width={30}
                    height={30}
                  />
                </BodyBlockWrapper>
              )
            }

            if (card.value.type === 'audio') {
              return (
                <BodyBlockWrapper parent={block} block={card} key={card.id}>
                  <BlockIcon
                    block={card}
                    iconProps={{
                      className: 'mr-1 shrink-0',
                      width: 16,
                      height: 16,
                    }}
                  />
                  {card.value.fileName}
                </BodyBlockWrapper>
              )
            }

            return (
              <BodyBlockWrapper parent={block} block={card} key={card.id}>
                <div className="flex items-center space-x-1 rounded-lg border border-dark-border-solid group-hover:border-dark-elevated py-0.5 pl-1 pr-0.5">
                  <FileTextIcon
                    className="inline-block shrink-0 text-dark-muted"
                    width={16}
                    height={16}
                  />
                  <div className="flex items-center overflow-hidden">
                    <p className="text-xs truncate text-dark-muted">
                      {card.value.fileName}
                    </p>
                    <p className="text-xs leading-none text-dark-dim whitespace-nowrap">
                      {formatBytes(card.value.fileSize)}
                    </p>
                  </div>
                </div>
              </BodyBlockWrapper>
            )

          case 'color': {
            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="py-1 rounded-lg"
              >
                <div className="flex items-center text-xs text-dark-muted">
                  <span
                    key={card.id}
                    className="w-5 h-5 border border-dark-border rounded"
                    style={{ backgroundColor: card.value }}
                  />
                  &nbsp;&nbsp;
                  {card.value}
                </div>
              </BodyBlockWrapper>
            )
          }
          case 'boolean': {
            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="py-1 rounded-md"
              >
                <Checkbox checked={card.value} />
              </BodyBlockWrapper>
            )
          }
          case 'number':
          case 'text':
            if (!card.value) return null
            return (
              <BodyBlockWrapper parent={block} block={card} key={card.id}>
                <p className="text-xs text-dark-muted leading-normal line-clamp-2">
                  {card.value}
                </p>
              </BodyBlockWrapper>
            )

          case 'rich-text': {
            if (!card.value) return null
            const base64 = stateToBase64(
              card.value?.state ?? '',
              richTextExtensionsForTransformations
            )

            const yDoc = buildYDocFromState(base64)
            const plainText = yDocToPlainText(
              yDoc,
              richTextExtensionsForTransformations
            )

            if (!plainText) return null

            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="text-xs text-dark-muted leading-normal"
              >
                <div className="line-clamp-6">{plainText}</div>
              </BodyBlockWrapper>
            )
          }

          case 'code-snippet': {
            const code = card.value.code
            if (!code) return null

            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="text-xs text-dark-muted leading-normal line-clamp-2 font-mono"
              >
                {card.value.code}
              </BodyBlockWrapper>
            )
          }

          case 'reference': {
            const referencesToShow = card.value.targetBlockIds.slice(0, 3)
            const remainingReferences = card.value.targetBlockIds.length - 3

            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="text-dark-muted flex-wrap gap-y-0.5 gap-x-3 leading-4 py-1"
              >
                Edit {card.title ? `${card.title}` : 'reference'}
                {false &&
                  referencesToShow.map((tbId, index) => {
                    const tb = treeManager?.getNode(tbId)?.value
                    if (!tb) return null
                    const ellipsis = `${tb.title || 'Untitled'}${
                      index === referencesToShow.length - 1
                        ? remainingReferences > 0
                          ? `, and ${remainingReferences} more`
                          : ''
                        : ', '
                    }`

                    return (
                      <span key={tb.id} className="flex">
                        <BlockIcon
                          block={tb}
                          iconProps={{
                            className: 'mr-1 shrink-0',
                            width: 14,
                            height: 14,
                          }}
                        />
                        {ellipsis}
                      </span>
                    )
                  })}
              </BodyBlockWrapper>
            )
          }
          case 'select': {
            const value = card.value.join(', ')
            if (!value) return null

            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="text-xs text-dark-muted leading-normal line-clamp-3"
              >
                {value}
              </BodyBlockWrapper>
            )
          }
          case 'document':
          case 'instance':
          case 'component':
            if (!card.value) return null

            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="text-xs text-dark-muted leading-normal line-clamp-1"
              >
                <BlockIcon
                  block={card}
                  iconProps={{
                    className: 'mr-1 shrink-0',
                    width: 16,
                    height: 16,
                  }}
                />
                {card.title || 'Untitled'}
              </BodyBlockWrapper>
            )
          case 'date':
            if (!card.value) return null

            return (
              <BodyBlockWrapper
                parent={block}
                block={card}
                key={card.id}
                className="text-xs text-dark-muted leading-normal line-clamp-1"
              >
                {isoStringToDisplayDate(new Date(card?.value).toISOString(), {
                  useTime: (blockFromTemplate as DateBlock).typeOptions.useTime,
                })}
              </BodyBlockWrapper>
            )
          default:
            return null
        }
      })
      .filter(Boolean)
  }, [
    treeManager,
    parent.typeOptions.targetBlockId,
    selectedView.thumbnailColumn,
    columns,
    block,
    isRenaming,
    isTemplate,
  ])

  const thumbnailIsIncompatibleWithView =
    thumbnailBlock === '__title' || thumbnailBlock.type !== 'media'

  // without context menu
  if (!contextMenu) {
    return (
      <Button
        unstyled
        focusStyled
        data-block-id={block.id}
        className={clsx(
          !!active?.id && 'pointer-events-none',
          'border border-dark-border-solid focus:outline bg-dark-base rounded-lg overflow-hidden text-left cursor-default group min-h-18',
          block.hidden && 'opacity-40',
          className
        )}
        ref={draggable.setNodeRef}
        {...draggable.attributes}
        {...draggable.listeners}
        onClick={(e) => {
          if (e.metaKey) {
            return window.open(`${tabPrefix}${idToSourceBlockId(block)}`)
          }
          addTab({
            key: `${tabPrefix}${idToSourceBlockId(block)}`,
            partiallyOpen: true,
          })
        }}
      >
        <div className="flex flex-col h-full">
          {!thumbnailIsIncompatibleWithView && (
            <figure
              style={{ aspectRatio: CARD_ASPECT_RATIO }}
              className="overflow-hidden relative flex items-center justify-center"
            >
              <CardThumbnail thumbnailBlock={thumbnailBlock} />
            </figure>
          )}
          {Boolean(body?.length) && columns.length && (
            <div
              className={clsx(
                !thumbnailIsIncompatibleWithView && 'border-t',
                'border-dark-border-solid py-2 px-3 space-y-2 flex-1 bg-dark-shade group-hover:bg-dark-border-solid'
              )}
            >
              {body}
            </div>
          )}
        </div>
      </Button>
    )
  }

  return (
    <div
      {...draggable.attributes}
      {...draggable.listeners}
      className={clsx(
        className,
        !!active?.id && 'pointer-events-none',
        block.hidden && 'opacity-40',
        'relative flex flex-col'
      )}
      // eslint-disable-next-line react-compiler/react-compiler
      ref={mergeRefs([draggable.setNodeRef, cardRef])}
    >
      <div
        className="absolute w-full pointer-events-none select-none"
        style={
          thumbnailIsIncompatibleWithView
            ? { height: 0 }
            : { aspectRatio: CARD_ASPECT_RATIO }
        }
      >
        <Input
          size="sm"
          type="text"
          autoFocus
          disabled={!isRenaming}
          role="card-title-input"
          tabIndex={-1}
          placeholder="Untitled"
          style={{ bottom: '-32px' }}
          className={clsx(
            'absolute max-h-6 inset-x-2 !w-auto pointer-events-auto !px-1.5',
            !isRenaming && 'invisible pointer-events-none select-none'
          )}
          onClick={(e) => {
            e.stopPropagation()
          }}
          onBlur={(e) => {
            e.stopPropagation()
            onFinishedRenaming?.(e.currentTarget.value)
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault()
              onFinishedRenaming?.()
            } else if (e.key === 'Enter') {
              onFinishedRenaming?.(e.currentTarget.value)
              addTab({
                key: `${tabPrefix}${idToSourceBlockId(block)}`,
                partiallyOpen: true,
              })
            }
          }}
          onChange={(e) => onChange(e.target.value)}
          {...syncedInputProps}
        />
      </div>

      <EditorModeProvider block={block} parentBlock={parent}>
        <BlockWrapperContextMenu
          block={block}
          parentBlock={parent}
          path={path}
          canOpenPropertiesPanel={false}
        >
          <Button
            unstyled
            data-block-id={block.id}
            focusStyled
            className="border border-dark-border-solid focus:outline bg-dark-base rounded-lg overflow-hidden text-left cursor-default group min-h-18 flex-1"
            onClick={(e) => {
              if (e.metaKey) {
                return window.open(`${tabPrefix}${idToSourceBlockId(block)}`)
              }
              addTab({
                key: `${tabPrefix}${idToSourceBlockId(block)}`,
                partiallyOpen: true,
              })
            }}
          >
            <div className="flex flex-col h-full">
              {!thumbnailIsIncompatibleWithView && (
                <figure
                  style={{ aspectRatio: CARD_ASPECT_RATIO }}
                  className="overflow-hidden relative flex items-center justify-center"
                >
                  <CardThumbnail thumbnailBlock={thumbnailBlock} />
                </figure>
              )}
              {((Boolean(body?.length) && columns.length) || isRenaming) && (
                <div
                  className={clsx(
                    !thumbnailIsIncompatibleWithView && 'border-t',
                    'border-dark-border-solid py-2 px-3 space-y-2 flex-1 bg-dark-shade group-hover:bg-dark-border-solid min-h-10'
                  )}
                >
                  {body}
                </div>
              )}
            </div>
          </Button>
        </BlockWrapperContextMenu>
      </EditorModeProvider>
    </div>
  )
}

const CardThumbnail = ({
  thumbnailBlock,
}: {
  thumbnailBlock: ReturnType<typeof useThumbnail>
}) => {
  if (thumbnailBlock === '__title' || thumbnailBlock.type !== 'media') {
    return null
  }

  if (!thumbnailBlock) {
    return (
      <div className="w-full h-full bg-dark-base flex items-center justify-center">
        <p className="text-dark-muted text-xs font-medium">
          No preview selected
        </p>
      </div>
    )
  }

  if (thumbnailBlock.value.status === 'idle') return null
  if (thumbnailBlock.value.status === 'uploading') {
    return (
      <div className="bg-dark-base w-full h-full absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
        <p className="text-dark-muted text-xs font-medium">
          {`${prettifyBlockType(thumbnailBlock.type)} being uploaded`}
        </p>
      </div>
    )
  }

  if (thumbnailBlock.value.type === 'image') {
    return (
      <BaseHubImage
        style={{
          backgroundImage: `url(data:image/png;base64,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)`,
          backgroundRepeat: 'repeat',
          backgroundSize: '24px',
        }}
        src={thumbnailBlock.value.url}
        className="object-scale-down min-w-full min-h-full select-none pointer-events-none"
        quality={100}
        width={222}
        height={110}
        alt={thumbnailBlock.value?.altText ?? ''}
      />
    )
  }

  if (thumbnailBlock.value.type !== 'video') return null
  return (
    <video
      src={thumbnailBlock.value.url}
      style={{
        backgroundImage: `url(data:image/png;base64,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)`,
        backgroundRepeat: 'repeat',
        backgroundSize: '16px',
      }}
      controls={false}
      autoPlay
      playsInline
      muted
      className="pointer-events-none select-none min-w-full min-h-full"
    />
  )
}

const BodyBlockWrapper = ({
  parent,
  block,
  children,
  className,
}: {
  parent: InstanceBlock | ComponentBlock
  block: Block
  children: React.ReactNode
  className?: string
}) => {
  const addTab = useTabsStore((s) => s.addTab)
  const { activeTabKey: blockId } = useDashboardSlugs()
  const setFocus = useRepoStore((s) => s.setFocus)
  const { teamSlug, repoSlug, branchOrCommit, repoPage } = useDashboardSlugs()
  const getNode = useWorkingTreeStore((s) => s.tree?.getNode)
  const activeDocument = blockId
    ? getNode?.(blockId, { fallbackKey: 'sourceBlockId' })?.value
    : null
  const parentSourceBlockId = idToSourceBlockId(parent)
  const sourceBlockId = idToSourceBlockId(block)

  return (
    <SimpleTooltip side="left" content={block.title}>
      <ButtonLink
        href={`/${teamSlug}/${repoSlug}/${repoPage}/${encodeURIComponent(
          branchOrCommit ?? ''
        )}/${parentSourceBlockId}#${sourceBlockId}`}
        unstyled
        key={block.id}
        data-block-id={sourceBlockId}
        tabIndex={-1}
        className={clsx(
          'flex-1 cursor-default text-xs px-1 focus:outline-none pointer-events-auto hover:bg-dark-elevated rounded flex items-center max-w-max',
          className
        )}
        onClick={(e) => {
          e.stopPropagation()
          let temporaryBackLink = undefined
          if (activeDocument) {
            temporaryBackLink = {
              title: activeDocument?.title || 'Untitled',
              id: idToSourceBlockId(activeDocument),
              edgeId: sourceBlockId,
            }
          }
          setFocus({
            type: 'block:wrapper',
            blockId: sourceBlockId,
            temporaryBackLink,
          })
          if (e.metaKey) return
          addTab({
            key: idToSourceBlockId(parent),
            partiallyOpen: true,
          })
        }}
      >
        {children}
      </ButtonLink>
    </SimpleTooltip>
  )
}
